import React, { useEffect } from 'react';
import {
    ShieldCheck,
    Layers,
    AlertTriangle,
    ImageOff,
    FileText,
    Crown,
    Repeat,
    CameraOff,
    Filter,
    Compass,
    LayoutGrid,
    Bot,
    Film,
    Camera,
    Megaphone,
    Workflow,
    Video,
    ListChecks,
    ClipboardCheck,
    MessageSquare,
    CheckCircle2,
    XCircle
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function AbhinavPersonalBrand() {

    useEffect(() => {
        document.title = "AbhiiFit | Distribution Engine";
        window.scrollTo(0, 0);
    }, []);

    const funnelSteps = [
        { n: '01', h: 'Someone introduces you', s: 'The warm intro is the lead. This is how your tier actually buys, on trust, through people they already know.' },
        { n: '02', h: 'They open your Instagram', s: 'Your profile is the first impression. Right now it is the de-facto landing page, by accident, telling a broad-fitness story.' },
        { n: '03', h: 'They see your presentation', s: 'Grid, pinned reels, bio and DM flow, all engineered to read as Neuro-Performance for UAE HNI.' },
        { n: '04', h: 'A discovery call gets booked', s: 'The funnel does the qualifying. You meet people already sold on who you are.' }
    ];

    const wont = [
        { h: 'A number of leads, calls, or clients', s: 'You do not have the data baseline on this positioning yet. We will not sell you certainty we cannot back.' },
        { h: 'Overnight HNI inbound', s: 'Trust-based buyers move on their own timeline, not the algorithm.' },
        { h: 'An AI clone in front of your buyer at the decision moment', s: 'Your real footage carries the VIP-facing proof. Always.' }
    ];

    const will = [
        { h: 'A content system that runs without you', s: 'AI clone trained on your likeness, combined with your own footage. Around 30 videos a month. You stop being the bottleneck.' },
        { h: 'Your profile rebuilt as a real funnel', s: 'The presentation every warm intro lands on, engineered to convert into a call.' },
        { h: 'Distribution and DM triage installed', s: 'Five platforms. Qualified HNI signals surface to you, the noise does not.' },
        { h: 'Content that compounds', s: 'As the data builds, the system pulls new HNI on top of your inner circle. Upside, not a quota.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">AbhiiFit</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        Built for now.<br />Designed to last.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-16 font-light">
                        A content system and private funnel for the AbhiiFit Neuro-Performance Method.<br className="hidden sm:block" /> Your profile becomes the presentation your next VIP client sees. Founder-led, AI-assisted, real footage at the core.
                    </p>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-6 py-8">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-3">Investment · 90 days</div>
                            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight">AED 18,000</div>
                            <div className="text-xs sm:text-sm text-white/50 mt-2">All inclusive · NDA before Day 1</div>
                        </div>
                    </div>
                </section>

                {/* HOW YOUR BUYER FINDS YOU */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The real funnel</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Your clients come from<br />your inner circle.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            HNI buyers move on trust, not reach. The AED 60K to 180K client comes from a warm introduction. Our job is to make sure that when the intro happens, what they find converts.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {funnelSteps.map((step) => (
                            <div key={step.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{step.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{step.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{step.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <p className="text-center text-xs sm:text-sm text-white/40 max-w-2xl mx-auto mt-10 italic font-light">
                        New HNI leads compound on top of this as the content builds. That is the upside, not the promise.
                    </p>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Where you are right now.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Layers} heading="Brand spread across every audience" subheading="Kids, elderly, athletes, post-stroke, corporate wellness, PCOS" accent="rose" />
                        <InfoCard icon={AlertTriangle} heading="Three sites tell three stories" subheading="Old site live · new rebrand empty · IG bio still on the old URL" accent="rose" />
                        <InfoCard icon={ImageOff} heading="Stock imagery, premium pricing" subheading="Your buyer can spot stock in three seconds" accent="rose" />
                        <InfoCard icon={FileText} heading="Walls of text where thesis should be" subheading="VIP clients scan for the problem and the solution, not paragraphs" accent="rose" />
                        <InfoCard icon={Crown} heading="AED 60-180K offer, broad-fitness presentation" subheading="The presentation has to match the price tag" accent="rose" />
                        <InfoCard icon={Repeat} heading="Wins without a system" subheading="VIP clients closed on referrals and reputation, nothing repeatable yet" accent="rose" />
                        <InfoCard icon={CameraOff} heading="Every video needs you on camera" subheading="So output stalls. No consistent presence, nothing compounds" accent="rose" />
                        <InfoCard icon={Filter} heading="No funnel to catch the intro" subheading="When someone checks you out, nothing is built to convert them into a call" accent="rose" />
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we build in 90 days.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-6xl mx-auto">
                        <InfoCard icon={Compass} heading="Brand thesis rebuild" subheading="Neuro-Performance for UAE HNI · not broad fitness" accent="indigo" />
                        <InfoCard icon={LayoutGrid} heading="Profile as your landing page" subheading="Grid, bio, pinned reels engineered to convert a warm intro" accent="indigo" />
                        <InfoCard icon={Bot} heading="AI clone content engine" subheading="Trained on your likeness, so videos ship without you on camera" accent="indigo" />
                        <InfoCard icon={Film} heading="Around 30 videos a month" subheading="Volume your schedule cannot produce alone" accent="indigo" />
                        <InfoCard icon={Camera} heading="Real-footage proof layer" subheading="Your actual client footage carries the VIP-facing credibility" accent="indigo" />
                        <InfoCard icon={Megaphone} heading="5-platform distribution" subheading="IG · YouTube Shorts · LinkedIn · TikTok · Facebook" accent="indigo" />
                        <InfoCard icon={Workflow} heading="Private funnel + DM triage" subheading="Intake, qualifying, calendar · HNI signals surface to you" accent="indigo" />
                        <InfoCard icon={ShieldCheck} heading="Dashboard + NDA" subheading="Live Notion metrics anytime · confidential by default" accent="indigo" />
                    </div>
                </section>

                {/* WHAT YOU'LL DO */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What you'll do for 90 days.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Lighter than you think. The clone carries the volume, so you are not chained to a weekly shoot.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Video} heading="Footage to feed the system" subheading="Periodic phone footage to train the clone and supply real proof moments" accent="emerald" />
                        <InfoCard icon={ListChecks} heading="Week 1 sign-off" subheading="ICP definition · profile direction · content pillars" accent="emerald" />
                        <InfoCard icon={ClipboardCheck} heading="NDA + likeness consent" subheading="Executed before Day 1, so we can build the clone" accent="emerald" />
                        <InfoCard icon={MessageSquare} heading="Monthly 30-min review" subheading="Your only standing meeting commitment" accent="emerald" />
                    </div>
                </section>

                {/* WHAT WE PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </section>

                {/* CLOSING TAGLINE */}
                <section className="text-center pt-4 sm:pt-8">
                    <p className="text-slate-400 text-sm sm:text-base italic font-light">
                        Built for now. Designed to last.
                    </p>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
